/**
 * useDemoBookCallTrigger — decides WHEN to nudge a demo visitor with the
 * soft "book a call" banner.
 *
 * The banner is intentionally lazy: we never show it on first landing (the
 * ``DemoWelcomeModal`` owns that moment). Instead we wait for an "aha" —
 * concrete evidence the user is engaged — before asking for their business
 * email. The trigger fires when ANY of these become true:
 *
 *   - ``uptime_5min``         : session has been live for ≥ 5 minutes
 *   - ``queries_3``           : user has sent ≥ 3 chat queries (total)
 *   - ``document_created``    : ≥ 1 document generation has started
 *   - ``connector_configured``: connector count has increased since mount
 *
 * Each trigger is persisted in ``demoBookCallStore.triggersFired`` so we
 * don't count the same "aha" twice across re-renders. The hook returns
 * ``shouldShow`` — a memoised boolean that flips true the moment ANY
 * trigger has been marked, provided the visitor hasn't already dismissed
 * OR converted.
 *
 * Non-demo sessions short-circuit to ``{shouldShow: false, triggerReason: null}``
 * so mounting the hook globally in the authed shell is safe.
 */
import * as React from "react";
import { useShallow } from "zustand/react/shallow";

import { useWsClient } from "@/features/api";
import { useActiveConnectorsCount } from "@/features/capabilities";
import { useChatStore } from "@/features/chat";

import {
  type DemoBookCallTrigger,
  useDemoBookCallStore,
} from "./demoBookCallStore";
import { useOnboardingMode } from "./useOnboardingMode";

/** Minutes the sandbox must be live before the ``uptime_5min`` trigger fires. */
const UPTIME_TRIGGER_MINUTES = 5;
/** Poll interval for the uptime check (10s is plenty for a 5-minute threshold). */
const UPTIME_POLL_MS = 10_000;
/** Query-count threshold for the ``queries_3`` trigger. */
const QUERY_TRIGGER_COUNT = 3;

interface TriggerResult {
  readonly shouldShow: boolean;
  /** Name of the FIRST trigger that fired; useful for analytics / session metadata. */
  readonly triggerReason: DemoBookCallTrigger | null;
}

export function useDemoBookCallTrigger(): TriggerResult {
  const isDemo = useOnboardingMode() === "demo";

  // Persisted flags — never re-prompt if the user has dismissed or converted.
  const { dismissed, captured, triggersFired } = useDemoBookCallStore(
    useShallow((s) => ({
      dismissed: s.dismissed,
      captured: s.captured,
      triggersFired: s.triggersFired,
    })),
  );
  const markTrigger = useDemoBookCallStore((s) => s.markTrigger);

  // Session-start timestamp — captured ONCE on mount; a reload resets it,
  // which is fine (the reload IS a fresh chance for the "aha" moment).
  const startedAtRef = React.useRef<number>(Date.now());

  const suppressed = !isDemo || dismissed || captured;

  // ── Trigger 1: uptime ≥ 5 min ────────────────────────────────────────────
  React.useEffect(() => {
    if (suppressed) return;
    if (triggersFired.includes("uptime_5min")) return;
    const check = (): void => {
      const elapsedMin = (Date.now() - startedAtRef.current) / 60_000;
      if (elapsedMin >= UPTIME_TRIGGER_MINUTES) {
        markTrigger("uptime_5min");
      }
    };
    // Fire once immediately (covers a re-mount after the threshold has passed)
    // then poll on a slow tick — 10s is imperceptible and cheap.
    check();
    const id = window.setInterval(check, UPTIME_POLL_MS);
    return () => window.clearInterval(id);
  }, [suppressed, triggersFired, markTrigger]);

  // ── Trigger 2: ≥ 3 chat queries sent ─────────────────────────────────────
  //
  // A "query" = one ``role: "user"`` message. We count across ALL
  // conversations (not just the current one) so the threshold survives a
  // "New chat" click. Subscribing via a selector keeps re-renders scoped
  // to the counter changing (not every WS token flowing into a stream).
  const totalUserMessages = useChatStore((s) => {
    let n = 0;
    for (const list of Object.values(s.messagesByConversation)) {
      for (const m of list) {
        if (m.role === "user") n += 1;
      }
    }
    return n;
  });
  React.useEffect(() => {
    if (suppressed) return;
    if (triggersFired.includes("queries_3")) return;
    if (totalUserMessages >= QUERY_TRIGGER_COUNT) {
      markTrigger("queries_3");
    }
  }, [suppressed, triggersFired, totalUserMessages, markTrigger]);

  // ── Trigger 3: at least one document has been generated ─────────────────
  //
  // The BE fires ``doc_generation_started`` at the top of every author run.
  // We listen once, mark the trigger, and immediately unsubscribe — cheap
  // and idempotent. Not gated on ``suppressed`` inside the listener so a
  // trigger that fires mid-session is captured even if the banner is
  // (temporarily) suppressed for another reason.
  const ws = useWsClient();
  React.useEffect(() => {
    if (suppressed) return;
    if (triggersFired.includes("document_created")) return;
    const off = ws.onMessage("doc_generation_started", () => {
      markTrigger("document_created");
    });
    return off;
  }, [ws, suppressed, triggersFired, markTrigger]);

  // ── Trigger 4: connector count increased since mount ────────────────────
  //
  // The demo sandbox ships with a pre-seeded connector set, so counting the
  // absolute value would fire immediately. We snapshot the count on FIRST
  // non-zero read (baseline) and mark the trigger when it later exceeds
  // that baseline.
  const connectorCount = useActiveConnectorsCount();
  const baselineConnectorsRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (suppressed) return;
    if (triggersFired.includes("connector_configured")) return;
    if (baselineConnectorsRef.current === null) {
      // First non-zero read seeds the baseline; while the manifest is
      // still loading (count === 0 initial) we defer.
      baselineConnectorsRef.current = connectorCount;
      return;
    }
    if (connectorCount > baselineConnectorsRef.current) {
      markTrigger("connector_configured");
    }
  }, [suppressed, triggersFired, connectorCount, markTrigger]);

  // ── Derived: shouldShow + first-firing reason ────────────────────────────
  return React.useMemo<TriggerResult>(() => {
    if (suppressed) return { shouldShow: false, triggerReason: null };
    const first = triggersFired[0] ?? null;
    return { shouldShow: first !== null, triggerReason: first };
  }, [suppressed, triggersFired]);
}
